// A2F-997: shared mapping between the legacy TableStylePreset palette model
// and the executor FormatRule model. Kept in lockstep with
// backend/services/table_style_mapping.py — any change here must be mirrored
// there, and both must agree with validate-tag-rules.mjs's validateRule check
// and backend/services/rule_validator.py _validate_format.
//
// Rule id convention: tbl_<preset_id>. Migration-created rules use the same
// convention (idempotency: skip if id already present).

import type { FormatRule, TableStylePreset } from '../types'

export const TABLE_STYLE_RULE_ID_PREFIX = 'tbl_'

// Maps a TableStylePreset → FormatRule ready for profile.rules[].
// Non-lossy: every palette field maps to an apply group key, so round-
// tripping via formatRuleToTableStylePreset recovers the original values.
export function tableStylePresetToFormatRule(preset: TableStylePreset): FormatRule {
  // Treat a missing or empty altRowBg as "no alt row" — matches the Python
  // `preset.get("altRowBg") or default_cell_bg` semantics. An empty string
  // would otherwise compare unequal to the defaultCellBg hex string and emit
  // an evenRow group with a missing/undefined shading.color, breaking lockstep.
  const altRowOn =
    !!preset.altRowBg &&
    preset.altRowBg.toLowerCase() !== (preset.defaultCellBg ?? '#FFFFFF').toLowerCase()

  // allCells baseline: body font/color/shading + vertical alignment + spaceAfter.
  // headerRow and evenRow override these for their specific rows (role-group-wins
  // precedence in the executor: allCells applied first, then role group).
  const allCells: Record<string, string | number | boolean> = {
    'font.name': preset.bodyFont ?? 'Calibri',
    'font.size': preset.bodyFontSize ?? 10,
    'font.color': preset.bodyTextColor ?? '#333333',
    'font.bold': false,
    'shading.color': preset.defaultCellBg ?? '#FFFFFF',
    'cell.verticalAlignment': 'Center',
    'paragraphFormat.spaceAfter': 0,
  }

  const headerRow: Record<string, string | number | boolean> = {
    'shading.color': preset.headerBg ?? '#E7E6E6',
    'font.name': preset.headerFont ?? 'Calibri',
    'font.size': preset.headerFontSize ?? 10,
    'font.color': preset.headerTextColor ?? '#333333',
    'font.bold': preset.headerBold ?? true,
  }

  const apply: Record<string, Record<string, string | number | boolean>> = {
    allCells,
    headerRow,
  }

  if (altRowOn) {
    apply.evenRow = {
      'shading.color': preset.altRowBg!,
    }
  }

  // Always emit borders group; weight=0 is the "no visible border" state,
  // matching the old applyTableStyle behavior that always set border.type=Single
  // regardless of width.
  apply.borders = {
    color: preset.borderColor ?? '#999999',
    weight: preset.borderWidth ?? 0.5,
    style: 'Single',
  }

  return {
    id: TABLE_STYLE_RULE_ID_PREFIX + preset.id,
    type: 'format',
    tier: 'pro',
    privacy: 'local',
    label: preset.name,
    icon: 'grid',
    targets: ['tables'],
    apply,
  }
}

// Inverse: recover a TableStylePreset from an executor FormatRule produced by
// tableStylePresetToFormatRule (or a migration-created equivalent). Used by the
// configurator to hydrate the SectionTableStyles UI from stored format rules.
export function formatRuleToTableStylePreset(rule: FormatRule): TableStylePreset {
  const allCells = (rule.apply.allCells ?? {}) as Record<string, unknown>
  const headerRow = (rule.apply.headerRow ?? {}) as Record<string, unknown>
  const evenRow = (rule.apply.evenRow ?? {}) as Record<string, unknown>
  const borders = (rule.apply.borders ?? {}) as Record<string, unknown>

  const defaultCellBg = String(allCells['shading.color'] ?? '#FFFFFF')
  const altRowBg = evenRow['shading.color'] ? String(evenRow['shading.color']) : defaultCellBg

  // Strip the id prefix to recover the original preset id.
  const presetId = rule.id.startsWith(TABLE_STYLE_RULE_ID_PREFIX)
    ? rule.id.slice(TABLE_STYLE_RULE_ID_PREFIX.length)
    : rule.id

  return {
    id: presetId,
    name: rule.label,
    headerBg: String(headerRow['shading.color'] ?? '#E7E6E6'),
    headerFont: String(headerRow['font.name'] ?? 'Calibri'),
    headerFontSize: Number(headerRow['font.size'] ?? 10),
    headerTextColor: String(headerRow['font.color'] ?? '#333333'),
    headerBold: Boolean(headerRow['font.bold'] ?? true),
    bodyFont: String(allCells['font.name'] ?? 'Calibri'),
    bodyFontSize: Number(allCells['font.size'] ?? 10),
    bodyTextColor: String(allCells['font.color'] ?? '#333333'),
    defaultCellBg,
    altRowBg,
    borderColor: String(borders.color ?? '#999999'),
    borderWidth: Number(borders.weight ?? 0.5),
  }
}

// True when a rule was produced by tableStylePresetToFormatRule (or an
// equivalent migration). Used to separate table-style rules from user-authored
// rules in the configurator hydration and My Chrona rendering.
export function isTableStyleFormatRule(rule: {
  id: string
  type: string
  targets?: string[]
}): boolean {
  return (
    rule.type === 'format' &&
    rule.id.startsWith(TABLE_STYLE_RULE_ID_PREFIX) &&
    Array.isArray((rule as FormatRule).targets) &&
    (rule as FormatRule).targets.includes('tables')
  )
}
